import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Avatar,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogContent,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Radio,
    TextField,
    Typography,
} from '@mui/material';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import TheaterComedyOutlinedIcon from '@mui/icons-material/TheaterComedyOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined';
import type { Colors } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { FORM_IDENTIFIER_ENUM, ICastingDirector, ILocation } from '../../types/global/helper';
import { setSystemForm } from '../../redux/appConfigurator/action';
import { ThspianAvatar } from '../../components/Avatar/ThspianAvatar';
import PhotoUploadModal, { TImage } from '../../components/Feed/PhotoUploadModal';
import { RICH_MEDIA_SHARE_ASPECT } from '../../constants/richMediaShareImage';
import { RichMediaShareSizeHint } from '../../components/Common/RichMediaShareSizeHint';
import { CastingDirectorCard } from '../../components/SearchAdd/CastingDirectorCard';
import { brandsSelector } from '../../redux/buzzbridge/selectors';
import { getJWtDetails } from '../../utils/AppUtils';
import { normalizeCastingDirectorRecord } from '../../utils/castingDirectorNormalize';
import { useThemeContext } from '../../hook/useThemeManager';
import dayjs from 'dayjs';
import { useDropzone } from 'react-dropzone';

import {
    BRAND_LUXE_PRIMARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER_HIGHEST,
} from './brandDashboardTokens';
import { PlacesAutocompleteField } from '../../components/Common/PlacesAutocompleteField';
import type { PlacesSuggestionPalette } from '../../components/Common/PlacesAutocompleteField';
import { useGoogleMapsLoader } from '../../hook/useMapLoader';
import { CustomDatePicker } from '../../components/DatePicker/CustomDatePicker';
import { RichTextEditor } from '../../components/Common/RichTextEditor';
import { getAuditionSynopsisLabel, getAuditionSynopsisPlaceholder } from '../../utils/auditionRichTextCopy';
import { plainTextFromHtml } from './contestWizard/richTextUtils';
import { datePickerSx } from './ugcCampaignFormHelpers';

const AUDITION_TYPES = [
    { value: 'video', label: 'Video' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'film', label: 'Film' },
    { value: 'voice over', label: 'Voice Over' },
    { value: 'theatre', label: 'Theatre' },
    { value: 'print', label: 'Print' },
];

const GIG_TYPES = [
    { value: 'cast', label: 'Cast' },
    { value: 'crew', label: 'Crew' },
    { value: 'technical', label: 'Technical' },
    { value: 'mixed', label: 'Mixed' },
];

/** Normalize CustomDatePicker ISO output to `YYYY-MM-DD` for audition API fields. */
function datePickerValueToYyyyMmDd(val: string): string {
    if (!val) return '';
    const d = dayjs(String(val));
    return d.isValid() ? d.format('YYYY-MM-DD') : '';
}

const GIG_TYPE_ICONS: Record<string, React.ReactElement> = {
    cast: <TheaterComedyOutlinedIcon sx={{ fontSize: 18 }} />,
    crew: <Groups2OutlinedIcon sx={{ fontSize: 18 }} />,
    technical: <PrecisionManufacturingOutlinedIcon sx={{ fontSize: 18 }} />,
    mixed: <AltRouteOutlinedIcon sx={{ fontSize: 18 }} />,
};

const CREW_SKILL_OPTIONS = [
    'Camera',
    'Sound',
    'Lighting',
    'Grip',
    'Art Department',
    'Hair & Makeup',
    'Wardrobe',
    'Production',
    'Locations',
    'Transport',
];

const TECHNICAL_SKILL_OPTIONS = [
    'Editing',
    'Color Grading',
    'VFX',
    'Motion Graphics',
    'Sound Design',
    'DIT',
    'Encoding / Delivery',
];

const SKILL_OPTIONS = [
    'Acting',
    'Singing',
    'Dancing',
    'Voice Acting',
    'Modelling',
    'Presenting',
    'Comedy',
    'Stunts',
    'Improvisation',
];

const TYPE_OF_WORK_OPTIONS = [
    { value: 'ad campaign', label: 'Ad Campaign' },
    { value: 'short', label: 'Short' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'vertical short form', label: 'Vertical Short Form' },
    { value: 'episodic', label: 'Episodic' },
    { value: 'student film', label: 'Student Film' },
    { value: 'theatre', label: 'Theatre' },
    { value: "director's reel", label: "Director's Reel" },
    { value: 'feature', label: 'Feature' },
    { value: 'print', label: 'Print' },
    { value: 'pilot presentation', label: 'Pilot Presentation' },
    { value: 'table read', label: 'Table Read' },
    { value: 'live event', label: 'Live Event' },
];

const UNION_OPTIONS = [
    { value: 'AEA', label: 'AEA' },
    { value: 'SAG-AFTRA', label: 'SAG-AFTRA' },
    { value: 'non-union', label: 'Non-Union' },
    { value: 'not applicable', label: 'Not Applicable' },
    { value: 'ACTRA', label: 'ACTRA' },
    { value: 'CAEA', label: 'CAEA' },
    { value: 'Equity', label: 'Equity' },
    { value: 'MEAA', label: 'MEAA' },
    { value: 'non-union affiliation', label: 'Non-Union Affiliation' },
    { value: 'other', label: 'Other' },
    { value: 'UBCP/ACTRA', label: 'UBCP/ACTRA' },
    { value: 'UDA', label: 'UDA' },
];

const SUBMISSION_REPORT_OPTIONS = [
    { value: 'daily digest', label: 'Daily Digest' },
    { value: 'weekly digest', label: 'Weekly Digest' },
    { value: 'monthly digest', label: 'Monthly Digest' },
    { value: 'applications as they come in', label: 'Applications as they come in' },
    { value: 'prerequisite', label: 'Prerequisite' },
];

function skillsForGigType(gig: string): string[] {
    switch (gig) {
        case 'crew':
            return CREW_SKILL_OPTIONS;
        case 'technical':
            return TECHNICAL_SKILL_OPTIONS;
        case 'mixed':
            return Array.from(new Set([...SKILL_OPTIONS, ...CREW_SKILL_OPTIONS, ...TECHNICAL_SKILL_OPTIONS]));
        case 'cast':
        default:
            return SKILL_OPTIONS;
    }
}

const AUDITION_TITLE_PLACEHOLDERS = [
    'Create a job that thspians can apply to',
    'e.g Seeking a 6ft male model...',
    'Video editor for hire...',
    'Casting for a student film...',
];

function getFieldSx(t: Colors) {
    return {
        '& .MuiOutlinedInput-root': {
            bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
            borderRadius: 2,
            color: t.primary_white,
            '& fieldset': { border: 'none' },
            '&:hover fieldset': { border: 'none' },
            '&.Mui-focused fieldset': { border: 'none' },
            '&.Mui-focused': { boxShadow: '0 0 0 1px rgba(236,170,0,0.35)' },
        },
        '& .MuiInputBase-input': { color: t.primary_white },
        '& .MuiInputBase-input::placeholder': { color: t.form_label, opacity: 1 },
    };
}

function fieldErrorSx(missing: boolean) {
    if (!missing) return {};
    return { outline: '1.5px solid #ef4444', borderRadius: '8px' };
}

function shootAddressStringToILocation(addr: string): ILocation {
    const t = (addr || '').trim();
    const short = t.split(',')[0]?.trim() || t;
    return {
        type: 'Point',
        coordinates: [0, 0],
        address: t,
        description: t,
        short_name: short || t,
    };
}

export interface AuditionBasicsState {
    title: string;
    audition_type: string;
    gig_type: string;
    synopsis: string;
    plot: string;
    shoot_location: string;
    casting_expiration: string;
    job_start_date: string;
    min_age: string;
    max_age: string;
    required_skills: string[];
    tags: string[];
    type_of_work: string;
    union_affiliation: string;
    production_company: ICastingDirector | null;
    attachment: string;
    submission_report: string;
    attachment_raw: File | null;
    publishStatus?: 'publish' | 'draft' | 'schedule';
    scheduled_published_date?: string;
    imageCover?: File[] | string;
    is_background_color?: boolean;
    background_color?: string;
}

export interface BrandAuditionBasicsStepProps {
    themeColors: Colors;
    state: AuditionBasicsState;
    onChange: (field: keyof AuditionBasicsState, value: any) => void;
    /** Multi-step audition wizard: which page of basics UI to show (0–3). */
    wizardPage: 0 | 1 | 2 | 3;
    /** When true, highlight empty required fields in red. */
    showValidation?: boolean;
}

export function BrandAuditionBasicsStep({ themeColors: t, state, onChange, wizardPage, showValidation = false }: BrandAuditionBasicsStepProps) {
    const dispatch = useDispatch();
    const userBrands = useSelector(brandsSelector);
    const castingDirectors = userBrands
        .map(b => {
            const companyId: string | undefined =
                typeof b.company === 'string' ? b.company : (b.company as any)?._id ?? (b.company as any)?.id;
            if (!companyId) return null;
            return normalizeCastingDirectorRecord({ ...b, _id: companyId, company_name: b.name, profile_picture: b.logo ?? '' });
        })
        .filter(Boolean) as ICastingDirector[];
    const { device } = useThemeContext();
    const { user } = getJWtDetails();
    const { isLoaded: isMapsLoaded } = useGoogleMapsLoader();
    const [openPublishModal, setOpenPublishModal] = React.useState(false);
    const [publishStatus, setPublishStatus] = React.useState<'publish' | 'draft' | 'schedule'>(
        state.publishStatus || 'publish',
    );
    const [scheduledDate, setScheduledDate] = React.useState(
        state.scheduled_published_date || dayjs().add(2, 'day').format('YYYY-MM-DDTHH:mm'),
    );
    const [setAsDefault, setSetAsDefault] = React.useState(false);
    const [selectedColor, setSelectedColor] = React.useState<string | null>(null);
    const [isColorPickerExpanded, setIsColorPickerExpanded] = React.useState(false);
    const [coverMedia, setCoverMedia] = React.useState<TImage[]>([]);
    const [coverRawFiles, setCoverRawFiles] = React.useState<File[]>([]);
    const [titlePlaceholder, setTitlePlaceholder] = React.useState(AUDITION_TITLE_PLACEHOLDERS[0]);
    const [showColorComposer, setShowColorComposer] = React.useState(false);
    const fieldSx = getFieldSx(t);
    const showPage = (p: 0 | 1 | 2 | 3) => wizardPage === p;
    const labelSx = {
        fontSize: 11,
        fontFamily: FONT_ENUM.MAIN_BOLD,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.14em',
        color: t.primary_white,
        mb: 1.5,
    };

    const shootLocationSuggestionPalette: PlacesSuggestionPalette = {
        rowInactiveBg: BRAND_SURFACE_CONTAINER_HIGHEST,
        rowInactiveColor: 'rgba(255,255,255,0.9)',
        rowActiveBg: BRAND_LUXE_PRIMARY,
        rowActiveColor: '#392700',
        loadingBg: BRAND_SURFACE_CONTAINER_HIGHEST,
        loadingColor: 'rgba(255,255,255,0.9)',
        listBorder: `1px solid ${BRAND_OUTLINE_VARIANT}`,
    };

    const shootPlacesFieldSx = {
        '& .MuiOutlinedInput-root': {
            bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
            borderRadius: 2,
            color: t.primary_white,
            '& fieldset': { border: 'none' },
            '&:hover fieldset': { border: 'none' },
            '&.Mui-focused fieldset': { border: 'none' },
            '&.Mui-focused': { boxShadow: '0 0 0 1px rgba(236,170,0,0.35)' },
        },
        '& .MuiInputLabel-root': {
            color: 'rgba(255,255,255,0.65)',
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
        },
        '& .MuiOutlinedInput-input': { color: t.primary_white },
        '& .MuiOutlinedInput-input::placeholder': { color: t.form_label, opacity: 1 },
    };

    const autocompleteSx = {
        ...fieldSx,
        '& .MuiChip-root': {
            bgcolor: BRAND_LUXE_PRIMARY,
            color: '#5f4200',
            fontFamily: FONT_ENUM.MAIN_BOLD,
            fontSize: 12,
        },
        '& .MuiChip-deleteIcon': { color: '#5f4200' },
        '& .MuiSvgIcon-root': { color: t.primary_white },
    };

    const menuPaperSx = {
        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
        color: 'rgba(255,255,255,0.85)',
        backgroundImage: 'none',
        '& .MuiMenuItem-root': {
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
            fontSize: 14,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
            '&.Mui-selected': { bgcolor: `${BRAND_LUXE_PRIMARY}33` },
        },
    };

    const showUnionRow = state.gig_type !== 'technical';
    const synopsisMainLabel = getAuditionSynopsisLabel(state.gig_type);
    const synopsisPlaceholder = getAuditionSynopsisPlaceholder(state.gig_type);
    const skillChipOptions = React.useMemo(() => skillsForGigType(state.gig_type), [state.gig_type]);
    const prevGigRef = React.useRef(state.gig_type);
    React.useEffect(() => {
        if (prevGigRef.current === state.gig_type) return;
        prevGigRef.current = state.gig_type;
        const allowed = new Set(skillsForGigType(state.gig_type));
        onChange(
            'required_skills',
            state.required_skills.filter(s => allowed.has(s)),
        );
        if (state.gig_type !== 'cast' && state.gig_type !== 'mixed') {
            onChange('min_age', '');
            onChange('max_age', '');
        }
        if (state.gig_type === 'technical') {
            onChange('union_affiliation', '');
        }
    }, [state.gig_type, state.required_skills, onChange]);

    React.useEffect(() => {
        onChange('imageCover', coverRawFiles.length > 0 ? coverRawFiles : '');
        if (coverRawFiles.length > 0) {
            onChange('background_color', '');
            onChange('is_background_color', false);
        }
    }, [coverRawFiles, onChange]);

    React.useEffect(() => {
        onChange('publishStatus', publishStatus);
        if (publishStatus === 'schedule') {
            onChange('scheduled_published_date', scheduledDate);
            return;
        }
        onChange('scheduled_published_date', '');
    }, [publishStatus, scheduledDate, onChange]);

    const publishMeta =
        publishStatus === 'publish'
            ? {
                  title: 'Publish Immediately',
                  subtitle: 'Available to everyone on thspian',
              }
            : publishStatus === 'draft'
            ? {
                  title: 'Save as Draft',
                  subtitle: 'Publish at a later date',
              }
            : {
                  title: 'Scheduled Publish',
                  subtitle: `Publish on ${dayjs(scheduledDate).format('MMM D, YYYY h:mm A')}`,
              };
    const colorOptions = [
        '#000000',
        '#8A2BE2',
        '#DC143C',
        '#1B1B1B',
        'linear-gradient(45deg, #ff9a9e, #fad0c4)',
        'linear-gradient(45deg, #FFC371, #FF5F6D)',
        'linear-gradient(45deg, #4B79A1, #283E51)',
        'linear-gradient(45deg, #00C9FF, #92FE9D)',
        'linear-gradient(45deg, #f7971e, #ffd200)',
        'linear-gradient(45deg, #00F260, #0575E6)',
        'linear-gradient(45deg, #ee9ca7, #ffdde1)',
    ];
    const getColorPickerBg = () =>
        selectedColor || 'linear-gradient(45deg, #ff9a9e, #fad0c4, #fbc2eb, #a18cd1, #fbc2eb)';
    const onPdfDrop = React.useCallback(
        (accepted: File[]) => {
            const file = accepted[0];
            if (file && file.type === 'application/pdf') {
                onChange('attachment_raw', file);
                onChange('attachment', file.name);
            }
        },
        [onChange],
    );

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: onPdfDrop,
        accept: { 'application/pdf': ['.pdf'] },
        multiple: false,
    });

    const clearAttachment = React.useCallback(() => {
        onChange('attachment', '');
        onChange('attachment_raw', null);
    }, [onChange]);

    const attachmentCardSx = {
        borderRadius: '12px',
        border: `2px dashed ${BRAND_OUTLINE_VARIANT}50`,
        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
        transition: 'all 0.2s',
        minHeight: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        outline: 'none',
        '&:hover': {
            borderColor: `${BRAND_LUXE_PRIMARY}60`,
            bgcolor: 'rgba(255,201,101,0.04)',
        },
    };

    React.useEffect(() => {
        if (state.title?.trim()) return;
        let currentPhraseIndex = 0;
        let currentCharIndex = 0;
        const tick = () => {
            const phrase = AUDITION_TITLE_PLACEHOLDERS[currentPhraseIndex];
            if (currentCharIndex <= phrase.length) {
                setTitlePlaceholder(phrase.slice(0, currentCharIndex));
                currentCharIndex += 1;
                return;
            }
            currentCharIndex = 0;
            currentPhraseIndex = (currentPhraseIndex + 1) % AUDITION_TITLE_PLACEHOLDERS.length;
        };
        const id = window.setInterval(tick, 90);
        return () => window.clearInterval(id);
    }, [state.title]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            {showPage(0) && (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        {user && (
                            <Box
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: '50%',
                                    border: `2px solid ${BRAND_SURFACE_CONTAINER_HIGHEST}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    flexShrink: 0,
                                }}>
                                <ThspianAvatar
                                    user={user}
                                    disableVerified
                                    sx={{
                                        width: 38,
                                        height: 38,
                                    }}
                                />
                            </Box>
                        )}
                        <Box
                            onClick={() => setOpenPublishModal(true)}
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                borderRadius: '10px',
                                border: `1px solid ${BRAND_SURFACE_CONTAINER_HIGHEST}`,
                                backgroundColor: t.true_black,
                                px: 2,
                                py: 1.3,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                transition: 'border-color 0.2s ease',
                                cursor: 'pointer',
                                '&:hover': {
                                    borderColor: `${BRAND_LUXE_PRIMARY}88`,
                                },
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                <PublicOutlinedIcon sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 20 }} />
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography
                                        sx={{
                                            color: BRAND_LUXE_PRIMARY,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: { xs: 13, sm: 14 },
                                            lineHeight: 1.1,
                                        }}>
                                        {publishMeta.title}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: t.form_label,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 11,
                                            lineHeight: 1.1,
                                        }}>
                                        {publishMeta.subtitle}
                                    </Typography>
                                </Box>
                            </Box>
                            <ExpandMoreOutlinedIcon sx={{ color: t.form_label, fontSize: 22 }} />
                        </Box>
                    </Box>
                </Box>
            )}

            {showPage(0) && (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: { xs: 2, sm: 3 }, borderRadius: 2, ...fieldErrorSx(showValidation && !state.title.trim()) }}>
                    <Typography sx={labelSx}>Audition title</Typography>
                    <TextField
                        fullWidth
                        placeholder={state.title?.trim() ? 'Lead role for summer ad campaign' : titlePlaceholder}
                        value={state.title}
                        onChange={e => onChange('title', e.target.value)}
                        sx={{
                            ...fieldSx,
                            '& .MuiInputBase-input': {
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 18,
                                color: t.primary_white,
                            },
                        }}
                    />

                    <Box sx={{ mt: 2 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                borderRadius: '12px',
                                maxWidth: isColorPickerExpanded ? '100%' : '56px',
                                transition: 'max-width 0.3s ease-in-out',
                                overflowX: 'auto',
                                py: 0.5,
                            }}>
                            {isColorPickerExpanded && (
                                <IconButton
                                    onClick={() => setIsColorPickerExpanded(false)}
                                    sx={{ color: t.primary_white, mr: 0.5 }}>
                                    <ArrowBackIcon />
                                </IconButton>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                {isColorPickerExpanded &&
                                    colorOptions.map((color, index) => (
                                        <Box
                                            key={`${color}-${index}`}
                                            onClick={() => {
                                                setSelectedColor(color);
                                                setShowColorComposer(true);
                                                onChange('background_color', color);
                                                onChange('is_background_color', true);
                                                onChange('imageCover', 'no-image');
                                            }}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '8px',
                                                background: color,
                                                cursor: 'pointer',
                                                border:
                                                    selectedColor === color
                                                        ? `2px solid ${BRAND_LUXE_PRIMARY}`
                                                        : '2px solid transparent',
                                                flexShrink: 0,
                                            }}
                                        />
                                    ))}
                                {!isColorPickerExpanded && (
                                    <IconButton sx={{ p: 0 }} onClick={() => setIsColorPickerExpanded(true)}>
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: '10px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: getColorPickerBg(),
                                                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.3)',
                                            }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 18,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    color: 'white',
                                                    textShadow: '0px 1px 2px rgba(0, 0, 0, 0.3)',
                                                }}>
                                                Aa
                                            </Typography>
                                        </Box>
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {showColorComposer && selectedColor && (
                        <Box
                            sx={{
                                mt: 1.5,
                                width: '100%',
                                minHeight: 320,
                                borderRadius: '14px',
                                background: selectedColor,
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 3,
                            }}>
                            <IconButton
                                onClick={() => {
                                    setShowColorComposer(false);
                                    setSelectedColor(null);
                                    onChange('background_color', '');
                                    onChange('is_background_color', false);
                                }}
                                sx={{ color: t.primary_white, position: 'absolute', top: 10, right: 10 }}>
                                <CloseIcon />
                            </IconButton>
                            <Box sx={{ width: '100%' }}>
                                <TextField
                                    fullWidth
                                    variant="standard"
                                    placeholder={AUDITION_TITLE_PLACEHOLDERS[0]}
                                    value={state.title}
                                    onChange={e => onChange('title', e.target.value.slice(0, 90))}
                                    InputProps={{
                                        disableUnderline: true,
                                        style: {
                                            color: 'rgba(255,255,255,0.92)',
                                            fontSize: device.isMobile ? '34px' : '52px',
                                            textAlign: 'center',
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            lineHeight: 1.05,
                                        },
                                    }}
                                    inputProps={{ maxLength: 90, style: { textAlign: 'center' } }}
                                    sx={{
                                        '& .MuiInputBase-input::placeholder': {
                                            color: 'rgba(255,255,255,0.42)',
                                            opacity: 1,
                                        },
                                    }}
                                />
                                <Typography
                                    sx={{
                                        color: 'rgba(255,255,255,0.7)',
                                        fontSize: 16,
                                        position: 'absolute',
                                        right: 24,
                                        bottom: 18,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    {state.title?.length || 0}/90
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

            {showPage(0) && (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2, ...fieldErrorSx(showValidation && !state.type_of_work) }}>
                    <Typography sx={labelSx}>Type of work</Typography>
                    <Autocomplete
                        options={TYPE_OF_WORK_OPTIONS}
                        getOptionLabel={o => o.label}
                        value={TYPE_OF_WORK_OPTIONS.find(o => o.value === state.type_of_work) ?? null}
                        onChange={(_, v) => onChange('type_of_work', v?.value ?? '')}
                        renderInput={params => <TextField {...params} placeholder="Select type of work..." />}
                        sx={autocompleteSx}
                        slotProps={{
                            paper: { sx: menuPaperSx },
                        }}
                    />
                </Box>
            )}

            {showPage(0) && (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2, ...fieldErrorSx(showValidation && !state.audition_type) }}>
                    <Typography sx={labelSx}>Audition type</Typography>
                    <Autocomplete
                        options={AUDITION_TYPES}
                        getOptionLabel={o => o.label}
                        value={AUDITION_TYPES.find(o => o.value === state.audition_type) ?? null}
                        onChange={(_, v) => onChange('audition_type', v?.value ?? '')}
                        renderInput={params => <TextField {...params} placeholder="Select type..." />}
                        sx={autocompleteSx}
                        slotProps={{
                            paper: {
                                sx: {
                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                    color: 'rgba(255,255,255,0.7)',
                                    backgroundImage: 'none',
                                },
                            },
                        }}
                    />
                </Box>
            )}

            {showPage(0) && (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2, ...fieldErrorSx(showValidation && !state.gig_type) }}>
                    <Typography sx={labelSx}>What type of talent are you seeking</Typography>
                    <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST, p: 2, borderRadius: 2 }}>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, minmax(0, 1fr))' },
                                gap: 1.25,
                            }}>
                            {GIG_TYPES.map(({ value, label }) => {
                                const active = state.gig_type === value;
                                return (
                                    <Chip
                                        key={value}
                                        label={label}
                                        icon={GIG_TYPE_ICONS[value]}
                                        onClick={() => onChange('gig_type', value)}
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: { xs: 14, sm: 15 },
                                            height: { xs: 42, sm: 48 },
                                            borderRadius: 999,
                                            width: '100%',
                                            bgcolor: active ? BRAND_LUXE_PRIMARY : BRAND_SURFACE_CONTAINER_LOW,
                                            color: active ? '#5f4200' : t.primary_white,
                                            justifyContent: 'center',
                                            px: 1,
                                            '& .MuiChip-label': {
                                                px: 1,
                                            },
                                            '& .MuiChip-icon': {
                                                ml: 0.5,
                                                mr: 0.5,
                                                color: active ? '#5f4200' : 'rgba(255,255,255,0.85)',
                                            },
                                            '&:hover': { bgcolor: active ? BRAND_LUXE_PRIMARY : '#2c2c2c' },
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            )}

            {showPage(0) && (
                <Box
                    id="audition-synopsis"
                    sx={{
                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                        p: 3,
                        borderRadius: 2,
                        scrollMarginTop: { xs: 84, md: 96 },
                        ...fieldErrorSx(showValidation && plainTextFromHtml(state.synopsis).length === 0),
                    }}>
                    <Typography sx={labelSx}>{synopsisMainLabel}</Typography>
                    <RichTextEditor
                        value={state.synopsis}
                        onChange={value => onChange('synopsis', value)}
                        shellVariant="brandDashboard"
                        placeholder={synopsisPlaceholder}
                    />
                </Box>
            )}

            {showPage(3) && (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2, ...fieldErrorSx(showValidation && coverRawFiles.length === 0 && !state.imageCover) }}>
                    <Typography sx={labelSx}>
                        Add photo
                        <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
                        <RichMediaShareSizeHint inline sx={{ color: 'rgba(255,255,255,0.45)' }} />
                    </Typography>
                    {coverRawFiles.length === 0 && !state.imageCover && (
                        <Typography sx={{ fontSize: 11, color: 'error.main', mb: 1, fontFamily: 'inherit' }}>
                            Required — please upload a cover image
                        </Typography>
                    )}
                    <PhotoUploadModal
                        initialValue={coverMedia}
                        maxCount={1}
                        aspect={RICH_MEDIA_SHARE_ASPECT}
                        richMediaShare
                        onChange={(items, rawFiles) => {
                            setCoverMedia(items);
                            setCoverRawFiles(rawFiles || []);
                        }}
                        onCancelMediaUpload={() => {
                            setCoverMedia([]);
                            setCoverRawFiles([]);
                        }}
                        sx={{
                            border: 'none',
                            backgroundColor: BRAND_SURFACE_CONTAINER_HIGHEST,
                        }}
                        emptyStateSx={{
                            borderRadius: '12px',
                            border: `2px dashed ${BRAND_OUTLINE_VARIANT}50`,
                            bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                            transition: 'all 0.2s',
                            '&:hover': {
                                borderColor: `${BRAND_LUXE_PRIMARY}60`,
                                bgcolor: 'rgba(255,201,101,0.04)',
                            },
                        }}
                        emptyStateIconSx={{
                            backgroundColor: 'transparent',
                            '& .MuiSvgIcon-root': {
                                color: 'rgba(255,255,255,0.25)',
                            },
                        }}
                        emptyStateTitleSx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            color: 'rgba(255,255,255,0.4)',
                        }}
                        emptyStateSubtitleSx={{
                            fontSize: 11,
                            color: 'rgba(255,255,255,0.25)',
                            marginBottom: 0,
                        }}
                    />
                </Box>
            )}

            {showPage(0) && showUnionRow && (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                    <Typography sx={labelSx}>Union affiliation</Typography>
                    <TextField
                        select
                        fullWidth
                        value={state.union_affiliation}
                        onChange={e => onChange('union_affiliation', e.target.value)}
                        placeholder="Select union"
                        sx={fieldSx}
                        SelectProps={{
                            displayEmpty: true,
                            MenuProps: { PaperProps: { sx: menuPaperSx } },
                        }}>
                        <MenuItem value="">
                            <Typography sx={{ color: t.form_label, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                Select union (optional)
                            </Typography>
                        </MenuItem>
                        {UNION_OPTIONS.map(o => (
                            <MenuItem key={o.value} value={o.value}>
                                {o.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
            )}

            {showPage(3) && (
                <>
                    <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2, ...fieldErrorSx(showValidation && !state.production_company?._id) }}>
                        <Typography sx={labelSx}>Add a production company or agency</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Autocomplete
                                options={castingDirectors}
                                value={state.production_company}
                                onChange={(_, value) =>
                                    onChange('production_company', value ? normalizeCastingDirectorRecord(value) : null)
                                }
                                isOptionEqualToValue={(option, value) => !!value && option._id === value._id}
                                getOptionLabel={option => option.company_name || ''}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        placeholder="Search or select..."
                                        variant="outlined"
                                        sx={{
                                            ...fieldSx,
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                                borderRadius: 2,
                                                minHeight: 52,
                                                '& fieldset': { border: 'none' },
                                                '&:hover fieldset': { border: 'none' },
                                                '&.Mui-focused fieldset': { border: 'none' },
                                                '&.Mui-focused': {
                                                    boxShadow: '0 0 0 1px rgba(236,170,0,0.35)',
                                                },
                                            },
                                        }}
                                    />
                                )}
                                renderOption={(props, option) =>
                                    (() => {
                                        const { key, ...optionProps } = props;
                                        return (
                                            <Box
                                                component="li"
                                                key={option._id || String(key)}
                                                {...optionProps}
                                                sx={{
                                                    px: 2,
                                                    py: 1.5,
                                                    borderBottom: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                                                    borderRadius: 1,
                                                    mb: 0.75,
                                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                                                    '&.Mui-focused': { bgcolor: 'rgba(255,255,255,0.06)' },
                                                    '&:last-of-type': { mb: 0 },
                                                }}>
                                                <CastingDirectorCard castingDirector={option} mode="search" />
                                            </Box>
                                        );
                                    })()
                                }
                                slotProps={{
                                    paper: {
                                        sx: {
                                            ...menuPaperSx,
                                            mt: 1,
                                            border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                                            borderRadius: 1.5,
                                            p: 1,
                                            boxShadow: '0 10px 26px rgba(0,0,0,0.45)',
                                            '& .MuiAutocomplete-listbox': { p: 0 },
                                            '& .MuiAutocomplete-option:hover': {
                                                bgcolor: 'rgba(255,255,255,0.06)',
                                            },
                                            '& .MuiAutocomplete-option.Mui-focused': {
                                                bgcolor: 'rgba(255,255,255,0.06)',
                                            },
                                        },
                                    },
                                }}
                                sx={{ flex: 1 }}
                            />
                            <IconButton
                                onClick={() => dispatch(setSystemForm(FORM_IDENTIFIER_ENUM.CREATE_PRODUCTION_COMPANY))}
                                sx={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: 1.5,
                                    bgcolor: BRAND_LUXE_PRIMARY,
                                    color: '#5f4200',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    '&:hover': { bgcolor: '#f7ba47' },
                                }}>
                                <AddIcon sx={{ fontSize: 34, lineHeight: 1 }} />
                            </IconButton>
                        </Box>
                        {state.production_company ? (
                            <Box mt={1}>
                                <Chip
                                    avatar={
                                        <Avatar
                                            src={state.production_company.profile_picture}
                                            alt={state.production_company.company_name}
                                            sx={{ width: 32, height: 32 }}
                                        />
                                    }
                                    label={state.production_company.company_name}
                                    onDelete={() => onChange('production_company', null)}
                                    sx={{
                                        width: 'fit-content',
                                        maxWidth: '100%',
                                        height: 52,
                                        px: 1,
                                        borderRadius: 999,
                                        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                        border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                                        '& .MuiChip-label': {
                                            color: t.primary_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 16,
                                            px: 1.25,
                                        },
                                        '& .MuiChip-deleteIcon': {
                                            color: '#101216',
                                            backgroundColor: '#ef4444',
                                            borderRadius: '50%',
                                            fontSize: 18,
                                            p: 0.15,
                                            '&:hover': {
                                                color: '#101216',
                                                backgroundColor: '#dc2626',
                                            },
                                        },
                                    }}
                                />
                            </Box>
                        ) : null}
                    </Box>

                    <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                        <Typography sx={labelSx}>Add attachment</Typography>
                        <Paper
                            elevation={0}
                            {...getRootProps()}
                            sx={{
                                ...attachmentCardSx,
                                flexDirection: 'column',
                                p: 2,
                                position: 'relative',
                            }}>
                            <input {...getInputProps()} />
                            {state.attachment || state.attachment_raw ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        width: '100%',
                                        justifyContent: 'center',
                                        flexWrap: 'wrap',
                                    }}>
                                    <PictureAsPdfOutlinedIcon sx={{ fontSize: 40, color: BRAND_LUXE_PRIMARY }} />
                                    <Typography
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 14,
                                            color: t.primary_white,
                                            textAlign: 'center',
                                        }}>
                                        {state.attachment_raw?.name || state.attachment}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={e => {
                                            e.stopPropagation();
                                            clearAttachment();
                                        }}
                                        sx={{
                                            color: t.form_label,
                                            '&:hover': { color: BRAND_LUXE_PRIMARY, bgcolor: 'rgba(255,201,101,0.08)' },
                                        }}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        py: 1,
                                    }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 1.5,
                                        }}>
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: 'transparent',
                                            }}>
                                            <DriveFolderUploadOutlinedIcon
                                                sx={{ fontSize: 28, color: 'rgba(255,255,255,0.25)' }}
                                            />
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 14,
                                                letterSpacing: '0.08em',
                                                color: 'rgba(255,255,255,0.4)',
                                            }}>
                                            UPLOAD FILE
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: 11,
                                            color: 'rgba(255,255,255,0.25)',
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            textAlign: 'center',
                                        }}>
                                        Allowed formats: application/pdf
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Box>

                    <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                        <Typography sx={labelSx}>Submissions report</Typography>
                        <TextField
                            select
                            fullWidth
                            value={state.submission_report}
                            onChange={e => onChange('submission_report', e.target.value)}
                            sx={fieldSx}
                            SelectProps={{
                                MenuProps: { PaperProps: { sx: menuPaperSx } },
                            }}>
                            {SUBMISSION_REPORT_OPTIONS.map(o => (
                                <MenuItem key={o.value} value={o.value}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                </>
            )}

            {showPage(2) && (
                <Box
                    id="audition-shoot-location"
                    sx={{
                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                        p: 3,
                        borderRadius: 2,
                        scrollMarginTop: { xs: 84, md: 96 },
                        ...fieldErrorSx(showValidation && !state.shoot_location.trim()),
                    }}>
                    {isMapsLoaded ? (
                        <PlacesAutocompleteField
                            label="Shoot location"
                            placeholder="Start typing, then pick a suggestion"
                            value={shootAddressStringToILocation(state.shoot_location)}
                            onChange={loc => {
                                const single = Array.isArray(loc) ? loc[0] : loc;
                                onChange('shoot_location', single?.address?.trim() ?? '');
                            }}
                            textFieldSx={shootPlacesFieldSx}
                            suggestionPalette={shootLocationSuggestionPalette}
                        />
                    ) : (
                        <>
                            <Typography sx={labelSx}>Shoot location</Typography>
                            <TextField
                                fullWidth
                                placeholder="Los Angeles, CA"
                                value={state.shoot_location}
                                onChange={e => onChange('shoot_location', e.target.value)}
                                sx={fieldSx}
                            />
                        </>
                    )}
                </Box>
            )}

            {showPage(2) && (
                <Box
                    id="audition-dates"
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 2,
                        scrollMarginTop: { xs: 84, md: 96 },
                    }}>
                    <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2, ...fieldErrorSx(showValidation && !state.casting_expiration.trim()) }}>
                        <Typography sx={labelSx}>Casting deadline</Typography>
                        <CustomDatePicker
                            brandLuxe
                            value={state.casting_expiration || undefined}
                            onChange={val => onChange('casting_expiration', datePickerValueToYyyyMmDd(val))}
                            minDate={dayjs().startOf('day')}
                            withTime={false}
                            withTimezone={false}
                            sx={datePickerSx}
                        />
                    </Box>
                    <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2, ...fieldErrorSx(showValidation && !state.job_start_date.trim()) }}>
                        <Typography sx={labelSx}>Job start date</Typography>
                        <CustomDatePicker
                            brandLuxe
                            value={state.job_start_date || undefined}
                            onChange={val => onChange('job_start_date', datePickerValueToYyyyMmDd(val))}
                            minDate={(() => {
                                const today = dayjs().startOf('day');
                                if (!state.casting_expiration) return today;
                                const cast = dayjs(state.casting_expiration).startOf('day');
                                if (!cast.isValid()) return today;
                                return cast.isBefore(today) ? today : cast;
                            })()}
                            withTime={false}
                            withTimezone={false}
                            sx={datePickerSx}
                        />
                    </Box>
                </Box>
            )}

            {showPage(1) && (
                <Box
                    id="audition-tags"
                    sx={{
                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                        p: 3,
                        borderRadius: 2,
                        scrollMarginTop: { xs: 84, md: 96 },
                    }}>
                    <Typography sx={{ ...labelSx, mb: 2 }}>Required skills</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {skillChipOptions.map(skill => {
                            const active = state.required_skills.includes(skill);
                            return (
                                <Chip
                                    key={skill}
                                    label={skill}
                                    onClick={() =>
                                        onChange(
                                            'required_skills',
                                            active
                                                ? state.required_skills.filter(s => s !== skill)
                                                : [...state.required_skills, skill],
                                        )
                                    }
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 13,
                                        bgcolor: active ? BRAND_LUXE_PRIMARY : BRAND_SURFACE_CONTAINER_HIGHEST,
                                        color: active ? '#5f4200' : t.primary_white,
                                        '&:hover': { bgcolor: active ? BRAND_LUXE_PRIMARY : '#2c2c2c' },
                                    }}
                                />
                            );
                        })}
                    </Box>
                </Box>
            )}

            <Dialog
                open={openPublishModal}
                onClose={() => setOpenPublishModal(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                        backgroundImage: 'none',
                        borderRadius: 2,
                        border: `1px solid ${BRAND_SURFACE_CONTAINER_HIGHEST}`,
                    },
                }}>
                <DialogContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography
                            sx={{
                                color: t.primary_white,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 18,
                            }}>
                            Post Preference
                        </Typography>
                        <IconButton onClick={() => setOpenPublishModal(false)} sx={{ color: t.form_label }}>
                            <ExpandMoreOutlinedIcon sx={{ transform: 'rotate(180deg)' }} />
                        </IconButton>
                    </Box>

                    <Typography sx={{ mb: 2, color: t.form_label, fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 13 }}>
                        Who can see your post? Your post will show up in Feed, on your profile, and in search results.
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControlLabel
                            value="publish"
                            control={
                                <Radio
                                    checked={publishStatus === 'publish'}
                                    onChange={() => {
                                        setPublishStatus('publish');
                                        onChange('publishStatus', 'publish');
                                    }}
                                    sx={{ color: t.form_label, '&.Mui-checked': { color: BRAND_LUXE_PRIMARY } }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{ color: t.primary_white, fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14 }}>
                                    Publish Immediately - Immediately available to anyone on thspian
                                </Typography>
                            }
                        />
                        <FormControlLabel
                            value="draft"
                            control={
                                <Radio
                                    checked={publishStatus === 'draft'}
                                    onChange={() => {
                                        setPublishStatus('draft');
                                        onChange('publishStatus', 'draft');
                                    }}
                                    sx={{ color: t.form_label, '&.Mui-checked': { color: BRAND_LUXE_PRIMARY } }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{ color: t.primary_white, fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14 }}>
                                    Draft - Publish at a later date.
                                </Typography>
                            }
                        />

                        <FormControlLabel
                            value="schedule"
                            control={
                                <Radio
                                    checked={publishStatus === 'schedule'}
                                    onChange={() => {
                                        setPublishStatus('schedule');
                                        onChange('publishStatus', 'schedule');
                                        onChange('scheduled_published_date', scheduledDate);
                                    }}
                                    sx={{ color: t.form_label, '&.Mui-checked': { color: BRAND_LUXE_PRIMARY } }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        color: t.primary_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 14,
                                    }}>
                                    Scheduled Publish - Publish at a specified date
                                </Typography>
                            }
                        />
                        {publishStatus === 'schedule' && (
                            <TextField
                                type="datetime-local"
                                value={scheduledDate}
                                onChange={e => {
                                    setScheduledDate(e.target.value);
                                    onChange('scheduled_published_date', e.target.value);
                                }}
                                sx={{ ...fieldSx, mt: 1, ml: 4, width: { xs: '100%', sm: '70%' } }}
                            />
                        )}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={setAsDefault}
                                    onChange={e => setSetAsDefault(e.target.checked)}
                                    sx={{ color: t.form_label, '&.Mui-checked': { color: BRAND_LUXE_PRIMARY } }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{ color: t.primary_white, fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 13 }}>
                                    Set as default.
                                </Typography>
                            }
                        />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
                        <Button
                            onClick={() => setOpenPublishModal(false)}
                            sx={{
                                color: t.form_label,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                            }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => setOpenPublishModal(false)}
                            variant="contained"
                            sx={{
                                textTransform: 'none',
                                borderRadius: 1.5,
                                bgcolor: BRAND_LUXE_PRIMARY,
                                color: '#5f4200',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                '&:hover': { bgcolor: BRAND_LUXE_PRIMARY },
                            }}>
                            Done
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
}
